/**
 * metricFormat — the one place a KPI decides what an absent number looks like, and what
 * colour a number earns (TASK-872).
 *
 * TASK-872's first pass fixed four panels one at a time. It said so itself when it reopened:
 * "the null-to-dash rule is applied per panel, not at the formatter… the next KPI card added
 * will miss it for the same reason." It did — `Performance by Portfolio` ranked `+0.00%` best
 * and `+0.00%` worst, `Beta` printed a placeholder `1.00` as a measurement, `Unrealized P&L`
 * printed `+CHF 0` in green across 69 positions, and `Volatility 11.55%` rendered red directly
 * above its own caption saying the 20.00% threshold was not exceeded.
 *
 * **What this module does and does not close.** It makes the cards honest about a value they
 * are GIVEN. It cannot help where the caller replaces the null first: `RiskTab` still passes
 * `volatility || 0`, `beta || 1`, `sharpe_ratio || 0`, so `Beta 1.00` is still produced on that
 * surface — by the coercion, not by the card. Removing those, and the same `?? 0` / `?? 1.0` in
 * `ClientLevelView` and `useClientLevelData`, is TASK-897. A renderer cannot defend against a
 * value that was fabricated upstream; that is the lesson TASK-872 already recorded once, and
 * naming it here so this header is not read as a completeness claim.
 *
 * Two rules, and they are the whole module:
 *
 *   1. **Absent is not zero.** A null, an undefined or a NaN renders `—`, never `0`, and never
 *      carries a colour, a sign or an arrow. Four spellings of this existed across the
 *      codebase (`—`, `--`, `N/A`, `n/a`); `—` wins because every canvas widget already uses
 *      it, and `RiskAnalytics` ships a banner promising metrics are "shown as '—' rather than
 *      a misleading 0".
 *   2. **Colour follows the stated threshold.** A number inside the threshold printed beneath
 *      it is not red. `RiskMetricCard` computed `isExceeded` and then used it only for the
 *      caption, so the two halves of one card contradicted each other.
 *
 * The decoration rules are lifted from what TASK-872's first pass established rather than
 * reinvented — see `DualAttributionLayout` and `HistoryTab`:
 *
 *   - the sign follows the **displayed** value, so `0.04` at 1dp is not `+0.0%`;
 *   - exactly zero is a third state, grey — never green, never red, never signed;
 *   - each number's own value decides its own arrow and colour, not the list it sits in.
 */

/** What every surface prints when it has no number. */
export const METRIC_ABSENT = '—';

export type MetricKind = 'percent' | 'currency' | 'number' | 'ratio';

export interface FormatMetricOptions {
  kind?: MetricKind;
  /** Defaults per kind: percent 2, ratio 2, number 2, currency 0. */
  decimals?: number;
  currency?: string;
  /** Print a leading `+` on a positive DISPLAYED value. Never on zero. */
  signed?: boolean;
  locale?: string;
}

/**
 * A value that cannot be rendered as a measurement.
 *
 * A type predicate so a guard clause narrows the value for the caller — otherwise every call
 * site needs a cast, and a cast is how a null gets back in.
 */
export function isAbsent<T>(value: T | null | undefined): value is null | undefined {
  return value === null || value === undefined
    || (typeof value === 'number' && !Number.isFinite(value));
}

function defaultDecimals(kind: MetricKind): number {
  return kind === 'currency' ? 0 : 2;
}

/**
 * The value the reader actually sees, after rounding.
 *
 * The sign and the colour are decided on THIS, not on the raw number — otherwise `0.04`
 * printed to one decimal renders as `+0.0%`, a sign on a printed zero. Review caught exactly
 * that in this ticket's first pass.
 */
export function displayedValue(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  // `+0` rather than `-0`, so a tiny negative that rounds away does not print "-0.0".
  return (Math.round(value * factor) / factor) + 0;
}

export function formatMetric(
  value: number | null | undefined,
  options: FormatMetricOptions = {},
): string {
  if (isAbsent(value)) return METRIC_ABSENT;

  const kind = options.kind ?? 'number';
  const decimals = options.decimals ?? defaultDecimals(kind);
  const locale = options.locale ?? 'de-CH';
  const shown = displayedValue(value as number, decimals);
  const sign = options.signed && shown > 0 ? '+' : '';

  switch (kind) {
    case 'percent':
      return `${sign}${shown.toFixed(decimals)}%`;
    case 'currency':
      return sign + new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: options.currency ?? 'CHF',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(shown);
    case 'ratio':
    case 'number':
    default:
      return `${sign}${shown.toFixed(decimals)}`;
  }
}

/**
 * `absent`  — nothing to say. Grey, no arrow, no sign.
 * `neutral` — a real zero, or a value with no basis for judgement. Grey.
 * `positive` / `negative` — earned, either by direction or by a stated threshold.
 */
export type MetricTone = 'absent' | 'neutral' | 'positive' | 'negative';

/** Tailwind text colours. The class NAMES are asserted by existing suites — do not rename. */
export const TONE_TEXT_CLASS: Record<MetricTone, string> = {
  absent: 'text-gray-400',
  neutral: 'text-gray-900',
  positive: 'text-green-600',
  negative: 'text-red-600',
};

export const TONE_ARROW: Record<MetricTone, 'up' | 'down' | 'none'> = {
  absent: 'none',
  neutral: 'none',
  positive: 'up',
  negative: 'down',
};

/**
 * Tone by direction: up is good, down is bad, zero is neither.
 *
 * `decimals` matters — the judgement is on the displayed value, so a number that rounds to
 * zero reads neutral rather than positive.
 */
export function toneForDirection(
  value: number | null | undefined,
  decimals = 2,
  options: { reverse?: boolean } = {},
): MetricTone {
  if (isAbsent(value)) return 'absent';
  const shown = displayedValue(value as number, decimals);
  if (shown === 0) return 'neutral';
  const good = options.reverse ? shown < 0 : shown > 0;
  return good ? 'positive' : 'negative';
}

/**
 * Tone by the threshold the card prints underneath itself.
 *
 * This is the rule `RiskMetricCard` was missing. `Volatility 11.55%` under a stated `20.00%`
 * threshold is not a breach and must not be red, whatever bucket the caller's own `status`
 * heuristic put it in.
 *
 * `reverse` is for metrics where the threshold is a FLOOR rather than a ceiling — a Sharpe
 * ratio of 0.4 against a threshold of 1 is the weak side, while a volatility of 11.55 against
 * 20 is the safe side. Without it, every "higher is better" metric colours backwards.
 */
export function toneForThreshold(
  value: number | null | undefined,
  threshold: number | null | undefined,
  options: { reverse?: boolean; useAbsolute?: boolean } = {},
): MetricTone {
  if (isAbsent(value)) return 'absent';
  if (isAbsent(threshold)) return 'neutral';
  // Magnitude for a CEILING — a -24% drawdown breaches a 20 ceiling just as +24% does.
  // Signed for a FLOOR, and this is not a detail: review caught the first cut taking
  // `Math.abs` on both, so a Sharpe of -1.5 against a floor of 1 computed `1.5 < 1 === false`
  // and rendered in the same green as the best possible Sharpe. A negative Sharpe is an
  // ordinary losing period, so that was reachable on any real book.
  const useAbsolute = options.useAbsolute ?? !options.reverse;
  const v = useAbsolute ? Math.abs(value as number) : (value as number);
  const breached = options.reverse ? v < (threshold as number) : v > (threshold as number);
  return breached ? 'negative' : 'positive';
}

/** True when the card should print "Exceeded" beside its threshold. */
export function exceedsThreshold(
  value: number | null | undefined,
  threshold: number | null | undefined,
  options: { reverse?: boolean } = {},
): boolean {
  return toneForThreshold(value, threshold, options) === 'negative';
}
